// Google Analytics event tracking utility

// Declare gtag, rdt, dataLayer, and fbq functions for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    rdt: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
  }
}

// Utility function to validate email format (RFC 5321 compliant)
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

// Track custom events
export const trackEvent = (event: AnalyticsEvent) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.custom_parameters,
    });
  }
};

// Predefined event tracking functions for common actions
// Meta Pixel user identification
export const identifyUser = (email: string, additionalData?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    // Hash the email for privacy compliance
    const hashedEmail = btoa(email.toLowerCase().trim());
    
    // Track user identification event with hashed email
    window.fbq('track', 'UserIdentified', {
      em: hashedEmail, // hashed email
      ...additionalData
    });

    // Set advanced matching parameters for better tracking
    window.fbq('dataProcessingOptions', []);
    window.fbq('trackSingle', 'PIXEL_ID', 'PageView', {}, {
      em: hashedEmail,
      ...additionalData
    });
  }
};

// Reddit Pixel user identification
export const identifyUserReddit = (email: string, userId?: string) => {
  if (typeof window !== 'undefined' && window.rdt) {
    const cleanEmail = email.toLowerCase().trim();
    
    // Validate email format before sending to Reddit
    if (!isValidEmail(cleanEmail)) {
      console.warn('Invalid email format for Reddit tracking:', email);
      return;
    }
    
    // Set user data for Reddit Pixel
    window.rdt('track', 'UserIdentified', {
      em: cleanEmail, // Use raw email - Reddit will hash it properly
      external_id: userId || cleanEmail
    });
  }
};

// Google Analytics user identification
export const identifyUserGA = (email: string, userId?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Hash the email for privacy compliance
    const hashedEmail = btoa(email.toLowerCase().trim());
    
    // Set user ID for Google Analytics
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      user_id: userId || hashedEmail,
      custom_map: {
        user_email: hashedEmail
      }
    });

    // Track user identification event
    window.gtag('event', 'user_identified', {
      user_id: userId || hashedEmail,
      user_email: hashedEmail
    });
  }
};

// Google Tag Manager user identification
export const identifyUserGTM = (email: string, userId?: string, firstName?: string, lastName?: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Hash all user data for privacy compliance
    const hashedEmail = btoa(email.toLowerCase().trim());
    const hashedFirstName = firstName ? btoa(firstName.toLowerCase().trim()) : undefined;
    const hashedLastName = lastName ? btoa(lastName.toLowerCase().trim()) : undefined;
    
    // Push user data to dataLayer
    window.dataLayer.push({
      event: 'user_identified',
      user_id: userId || hashedEmail,
      user_email: hashedEmail,
      user_first_name: hashedFirstName,
      user_last_name: hashedLastName,
      user_data: {
        id: userId || hashedEmail,
        email: hashedEmail,
        firstName: hashedFirstName,
        lastName: hashedLastName
      }
    });
  }
};

// Set user data for all subsequent events
export const setUserData = (email: string, firstName?: string, lastName?: string) => {
  if (typeof window !== 'undefined' && window.fbq) {
    const userData = {
      em: btoa(email.toLowerCase().trim()), // hashed email
      fn: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
      ln: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
    };

    // Set user data for advanced matching
    window.fbq('trackSingle', 'PIXEL_ID', 'PageView', {}, userData);
  }
};

// Clear user identification (for logout)
export const clearUserIdentification = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    // Track logout event
    window.fbq('track', 'UserLogout');
  }
};

// Clear Reddit Pixel user identification
export const clearUserIdentificationReddit = () => {
  if (typeof window !== 'undefined' && window.rdt) {
    // Track logout event
    window.rdt('track', 'UserLogout');
  }
};

// Clear Google Analytics user identification
export const clearUserIdentificationGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Clear user ID
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      user_id: null
    });

    // Track logout event
    window.gtag('event', 'user_logout');
  }
};

// Clear Google Tag Manager user identification
export const clearUserIdentificationGTM = () => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Push logout event to dataLayer
    window.dataLayer.push({
      event: 'user_logout',
      user_id: null,
      user_email: null,
      user_first_name: null,
      user_last_name: null,
      user_data: null
    });
  }
};

export const analytics = {
  // User identification across all platforms
  // Note: login() and register() functions automatically call this, 
  // so only use this directly for non-auth scenarios (app load, token refresh, etc.)
  identifyUser: (email: string, firstName?: string, lastName?: string, userId?: string) => {
    // Meta Pixel identification
    setUserData(email, firstName, lastName);
    identifyUser(email, {
      fn: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
      ln: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
    });

    // Reddit Pixel identification
    identifyUserReddit(email, userId);

    // Google Analytics identification
    identifyUserGA(email, userId);

    // Google Tag Manager identification
    identifyUserGTM(email, userId, firstName, lastName);
  },

  // Navigation events
  pageView: (pageName: string) => {
    trackEvent({
      action: 'page_view',
      category: 'navigation',
      label: pageName,
    });
  },

  // Authentication events
  login: (method: 'email' | 'google', userEmail?: string, userId?: string, firstName?: string, lastName?: string) => {
    // Set up user identification FIRST across all platforms
    if (userEmail) {
      // Set up persistent user identification first
      setUserData(userEmail, firstName, lastName);
      identifyUser(userEmail, {
        fn: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
        ln: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
      });
      identifyUserReddit(userEmail, userId);
      identifyUserGA(userEmail, userId);
      identifyUserGTM(userEmail, userId, firstName, lastName);
    }

    // NOW track the event - Google Analytics will include user context
    trackEvent({
      action: 'login',
      category: 'auth',
      label: method,
    });

    // Track Reddit Pixel conversion with user data
    if (typeof window !== 'undefined' && window.rdt) {
      const eventData: any = {
        login_method: method
      };

      // Add user data for Reddit if available - use raw email
      if (userEmail) {
        const cleanEmail = userEmail.toLowerCase().trim();
        
        // Validate email format before sending to Reddit
        if (isValidEmail(cleanEmail)) {
          eventData.em = cleanEmail; // Use raw email for Reddit
          eventData.external_id = userId || cleanEmail;
        } else {
          console.warn('Invalid email format for Reddit Login tracking:', userEmail);
        }
      }

      window.rdt('track', 'Login', eventData);
    }

    // Track Facebook Pixel conversion with user data (already set above)
    if (typeof window !== 'undefined' && window.fbq) {
      const eventData: any = {
        login_method: method
      };

      // Add hashed email if available (already set above)
      if (userEmail) {
        eventData.em = btoa(userEmail.toLowerCase().trim());
      }

      window.fbq('track', 'Login', eventData);
    }

    // Track Google Analytics event with user data (already set above)
    if (typeof window !== 'undefined' && window.gtag && userEmail) {
      window.gtag('event', 'login', {
        method: method,
        user_id: userId || btoa(userEmail.toLowerCase().trim()),
        user_email: btoa(userEmail.toLowerCase().trim())
      });
    }

    // Track Google Tag Manager event (already set above)
    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventData: any = {
        event: 'user_login',
        login_method: method,
        event_category: 'auth',
        event_label: method
      };

      // Add hashed user data if available (already set above)
      if (userEmail) {
        eventData.user_id = userId || btoa(userEmail.toLowerCase().trim());
        eventData.user_email = btoa(userEmail.toLowerCase().trim());
      }

      window.dataLayer.push(eventData);
    }
  },

  register: (method: 'email' | 'google', userEmail?: string, firstName?: string, lastName?: string, userId?: string) => {
    // Set up user identification FIRST across all platforms
    if (userEmail) {
      // Set up persistent user identification first
      setUserData(userEmail, firstName, lastName);
      identifyUser(userEmail, {
        fn: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
        ln: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
      });
      identifyUserReddit(userEmail, userId);
      identifyUserGA(userEmail, userId);
      identifyUserGTM(userEmail, userId, firstName, lastName);
    }

    // NOW track the event - Google Analytics will include user context
    trackEvent({
      action: 'register',
      category: 'auth',
      label: method,
    });
    
    // Track Google Ads conversion with user data (already set above)
    if (typeof window !== 'undefined' && window.gtag) {
      const conversionData: any = {
        'send_to': 'AW-17655341904/LAwVCIT5x68bENDG3OJB'
      };

      // Add hashed user data if available (already set above)
      if (userEmail) {
        conversionData.user_id = userId || btoa(userEmail.toLowerCase().trim());
        conversionData.user_data = {
          email_address: btoa(userEmail.toLowerCase().trim()),
          first_name: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
          last_name: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
        };
      }

      window.gtag('event', 'conversion', conversionData);
    }

    // Track Reddit Pixel conversion with user data (already set above)
    if (typeof window !== 'undefined' && window.rdt) {
      const eventData: any = {
        registration_method: method
      };

      // Add user data if available - use raw email for Reddit
      if (userEmail) {
        const cleanEmail = userEmail.toLowerCase().trim();
        
        // Validate email format before sending to Reddit
        if (isValidEmail(cleanEmail)) {
          eventData.em = cleanEmail; // Use raw email for Reddit
          eventData.external_id = userId || cleanEmail;
        } else {
          console.warn('Invalid email format for Reddit SignUp tracking:', userEmail);
        }
      }

      window.rdt('track', 'SignUp', eventData);
    }

    // Track Facebook Pixel conversion with user data (already set above)
    if (typeof window !== 'undefined' && window.fbq) {
      const eventData: any = {
        registration_method: method
      };

      // Add hashed user data if available (already set above)
      if (userEmail) {
        eventData.em = btoa(userEmail.toLowerCase().trim());
      }
      if (firstName) {
        eventData.fn = btoa(firstName.toLowerCase().trim());
      }
      if (lastName) {
        eventData.ln = btoa(lastName.toLowerCase().trim());
      }

      window.fbq('track', 'CompleteRegistration', eventData);
    }

    // Track Google Analytics registration event with user data (already set above)
    if (typeof window !== 'undefined' && window.gtag && userEmail) {
      window.gtag('event', 'sign_up', {
        method: method,
        user_id: userId || btoa(userEmail.toLowerCase().trim()),
        user_email: btoa(userEmail.toLowerCase().trim()),
        user_first_name: firstName ? btoa(firstName.toLowerCase().trim()) : undefined,
        user_last_name: lastName ? btoa(lastName.toLowerCase().trim()) : undefined
      });
    }

    // Track Google Tag Manager event (already set above)
    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventData: any = {
        event: 'user_registration',
        registration_method: method,
        event_category: 'auth',
        event_label: method
      };

      // Add hashed user data if available (already set above)
      if (userEmail) {
        eventData.user_id = userId || btoa(userEmail.toLowerCase().trim());
        eventData.user_email = btoa(userEmail.toLowerCase().trim());
        eventData.user_first_name = firstName ? btoa(firstName.toLowerCase().trim()) : undefined;
        eventData.user_last_name = lastName ? btoa(lastName.toLowerCase().trim()) : undefined;
      }

      window.dataLayer.push(eventData);
    }
  },

  logout: () => {
    trackEvent({
      action: 'logout',
      category: 'auth',
    });

    // Clear user identification across all platforms
    clearUserIdentification(); // Meta Pixel
    clearUserIdentificationReddit(); // Reddit Pixel
    clearUserIdentificationGA(); // Google Analytics
    clearUserIdentificationGTM(); // Google Tag Manager
  },

  // Track management events
  trackUpload: (genre?: string) => {
    trackEvent({
      action: 'track_upload',
      category: 'track_management',
      label: genre,
    });

    // Track Google Tag Manager event
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'track_upload',
        track_genre: genre || 'unknown',
        event_category: 'track_management',
        event_label: genre
      });
    }
  },

  trackPlay: (trackId: string, source: 'get_feedback' | 'give_feedback' | 'track_manager') => {
    trackEvent({
      action: 'track_play',
      category: 'audio',
      label: source,
      custom_parameters: {
        track_id: trackId,
      },
    });
  },

  trackPause: (trackId: string, currentTime: number) => {
    trackEvent({
      action: 'track_pause',
      category: 'audio',
      custom_parameters: {
        track_id: trackId,
        playback_time: Math.round(currentTime),
      },
    });
  },

  // Feedback events
  feedbackSubmit: (rating: number, hasNotes: boolean) => {
    trackEvent({
      action: 'feedback_submit',
      category: 'feedback',
      value: rating,
      custom_parameters: {
        has_timestamped_notes: hasNotes,
      },
    });

    // Track Google Tag Manager event
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'feedback_submit',
        feedback_rating: rating,
        has_timestamped_notes: hasNotes,
        event_category: 'feedback',
        event_value: rating
      });
    }
  },

  feedbackRequest: (trackId: string) => {
    trackEvent({
      action: 'feedback_request',
      category: 'feedback',
      custom_parameters: {
        track_id: trackId,
      },
    });
  },

  // Genre filtering events
  genreFilter: (selectedGenres: string[], totalGenres: number) => {
    trackEvent({
      action: 'genre_filter',
      category: 'discovery',
      value: selectedGenres.length,
      custom_parameters: {
        selected_genres: selectedGenres.join(','),
        filter_percentage: Math.round((selectedGenres.length / totalGenres) * 100),
      },
    });
  },

  genreSelectAll: () => {
    trackEvent({
      action: 'genre_select_all',
      category: 'discovery',
    });
  },

  genreClearAll: () => {
    trackEvent({
      action: 'genre_clear_all',
      category: 'discovery',
    });
  },

  // Volume control events
  volumeChange: (volume: number) => {
    trackEvent({
      action: 'volume_change',
      category: 'audio',
      value: Math.round(volume * 100),
    });
  },

  muteToggle: (isMuted: boolean) => {
    trackEvent({
      action: 'mute_toggle',
      category: 'audio',
      label: isMuted ? 'muted' : 'unmuted',
    });
  },

  // Social media clicks
  socialClick: (platform: 'instagram' | 'reddit' | 'facebook' | 'youtube') => {
    trackEvent({
      action: 'social_click',
      category: 'engagement',
      label: platform,
    });
  },

  // Profile management
  profileUpdate: (section: 'bio' | 'links' | 'image') => {
    trackEvent({
      action: 'profile_update',
      category: 'profile',
      label: section,
    });
  },

  // Track status changes
  trackStatusChange: (newStatus: 'under_review' | 'published' | 'rejected') => {
    trackEvent({
      action: 'track_status_change',
      category: 'track_management',
      label: newStatus,
    });
  },

  // File upload events
  fileUploadStart: (fileType: string, fileSize: number) => {
    trackEvent({
      action: 'file_upload_start',
      category: 'upload',
      label: fileType,
      value: Math.round(fileSize / 1024 / 1024), // Size in MB
    });
  },

  fileUploadComplete: (fileType: string, uploadTime: number) => {
    trackEvent({
      action: 'file_upload_complete',
      category: 'upload',
      label: fileType,
      value: Math.round(uploadTime / 1000), // Time in seconds
    });
  },

  fileUploadError: (fileType: string, errorMessage: string) => {
    trackEvent({
      action: 'file_upload_error',
      category: 'upload',
      label: fileType,
      custom_parameters: {
        error_message: errorMessage,
      },
    });
  },
};

export default analytics;